import 'echarts/lib/chart/bar';
import 'echarts/lib/component/grid';
import 'echarts/lib/component/title';
import 'echarts/lib/component/tooltip';
import * as echarts from 'echarts/lib/echarts';
import React, { forwardRef, useImperativeHandle, useLayoutEffect, useRef } from 'react';
import format from 'date-fns/format';
import { UsageMetricsChartRef } from 'utils/types';

interface Metric {
  createdAt: string;
  classBOperations: number;
}

interface Bucket {
  id: string;
  name?: string;
  historicalBucketMetrics?: Metric[];
}

interface ClassBChartProps {
  buckets: Bucket[];
  start: string;
  end: string;
}

const ClassBChart = forwardRef<UsageMetricsChartRef, ClassBChartProps>(({ buckets, start, end }, ref) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const eChartInstance = useRef<echarts.ECharts | null>(null);

  useImperativeHandle(
    ref,
    () => ({
      getEChartInstance: () => eChartInstance.current,
    }),
    []
  );

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const utcDate = new Date(Date.UTC(date.getUTCMonth(), date.getUTCDate()));
    return format(utcDate, 'MMM d');
  };

  useLayoutEffect(() => {
    if (!chartRef.current) return;
    eChartInstance.current = echarts.init(chartRef.current);
    const myChart = eChartInstance.current;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // Generate all formatted dates between start and end
    const generateDateRange = (start: Date, end: Date) => {
      const dates: string[] = [];
      const current = new Date(start);
      while (current <= end) {
        const currentDay = new Date(current);
        currentDay.setHours(0, 0, 0, 0);
        if (currentDay <= today) {
          dates.push(formatDate(current.toISOString()));
        }
        current.setUTCDate(current.getUTCDate() + 1);
      }
      return dates;
    };
    let sortedDates = generateDateRange(new Date(start), new Date(end));
    sortedDates = sortedDates.map(formatDate);
    let seriesData: any[] = [];

    if (buckets && buckets.length > 0) {
      const allDates = new Set<string>();
      let hasMetrics = false;

      buckets.forEach((bucket) => {
        const metrics = bucket.historicalBucketMetrics || [];

        if (metrics.length > 0) {
          hasMetrics = true;
        }

        metrics.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

        metrics.forEach((metric) => {
          allDates.add(formatDate(metric.createdAt));
        });
      });

      // Use metrics-based dates only if there are any
      if (hasMetrics) {
        sortedDates = Array.from(allDates)
          .filter((dateStr) => {
            const parsed = new Date(`${dateStr}, ${today.getFullYear()}`);
            parsed.setHours(0, 0, 0, 0);
            return parsed <= today;
          })
          .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

        buckets.forEach((bucket) => {
          const metricsMap = new Map(
            (bucket.historicalBucketMetrics || []).map((m) => [formatDate(m.createdAt), m.classBOperations])
          );

          const data = sortedDates.map((date) => metricsMap.get(date) || 0);

          seriesData.push({
            name: bucket.name || bucket.id,
            type: 'bar',
            data,
            itemStyle: { barBorderRadius: [6, 6, 0, 0] },
          });
        });
      } else {
        // No metrics at all, use default dates with 0s
        seriesData = [
          {
            name: 'No Data',
            type: 'bar',
            data: sortedDates.map(() => 0),
          },
        ];
      }
    } else {
      // No buckets at all
      seriesData = [
        {
          name: 'No Data',
          type: 'bar',
          data: sortedDates.map(() => 0),
        },
      ];
    }

    const hasRealData = seriesData.some((series) => series.data.some((value: number) => value > 0));

    // ECharts options (same as your version)
    const option = {
      tooltip: {
        trigger: 'item',
        formatter: (params: any) =>
          `<strong>${params.seriesName}</strong><br/>Operations: ${params.value?.toFixed(5)}<br/>Date: ${params.name}`,
      },
      legend: {
        data: seriesData.map((s) => s.name),
        textStyle: { color: '#848F99' },
      },
      xAxis: {
        type: 'category' as const,
        data: sortedDates,
        axisLine: { lineStyle: { color: '#848F99' } },
        axisLabel: { rotate: 0, color: '#848F99' },
        axisTick: { lineStyle: { color: '#848F99' } },
      },
      dataZoom: [],
      grid: {
        left: '1%',
        right: '3%',
        bottom: '6%',
        containLabel: true,
      },
      yAxis: {
        type: 'value' as const,
        name: 'Operations',
        min: 0,
        ...(hasRealData ? {} : { max: 100 }),
        splitLine: {
          lineStyle: {
            color: '#EEF0F2',
          },
        },
        axisLine: { lineStyle: { color: '#848F99' } },
        axisLabel: { color: '#848F99' },
        axisTick: { lineStyle: { color: '#848F99' } },
      },
      series: seriesData,
    };

    myChart.setOption(option as any);
    const handleResize = () => myChart.resize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      myChart.dispose();
    };
  }, [buckets, start, end]);

  return (
    <div style={{ width: '100%' }}>
      <div ref={chartRef} style={{ width: '100%', height: '400px' }} />
    </div>
  );
});

export default ClassBChart;
